/**
 * Reusable show more/less functionality for collapsible text content
 * 
 * Usage:
 * Initialize with: initShowMore(containerSelector, options)
 * 
 * Options:
 * - wrapperSelector: selector for the wrapper element (default: '.description-wrapper')
 * - innerWrapperSelector: selector for the inner wrapper (default: '.description-inner-wrapper')
 * - textSelector: selector for the text element (default: '.description-text')
 * - buttonSelector: selector for the show more button (default: '.show-more')
 * - showMoreTextSelector: selector for show more text span (default: '.show-more-text')
 * - showLessTextSelector: selector for show less text span (default: '.show-less-text')
 * - collapsedHeight: height in pixels when collapsed (default: 48)
 * - collapsedClass: CSS class to add when collapsed (default: 'is-collapsed')
 */
function initShowMore(containerSelector, options = {}) {
  const {
    wrapperSelector,
    innerWrapperSelector = '.description-inner-wrapper',
    textSelector = '.description-text',
    buttonSelector = '.show-more',
    showMoreTextSelector = '.show-more-text',
    showLessTextSelector = '.show-less-text',
    collapsedHeight = 48,
    collapsedClass = 'is-collapsed'
  } = options;

  const containers = document.querySelectorAll(containerSelector);
  
  containers.forEach(container => {
    // If wrapperSelector is provided, use it; otherwise use container itself
    const wrapper = wrapperSelector ? container.querySelector(wrapperSelector) : container;
    const innerWrapper = innerWrapperSelector ? container.querySelector(innerWrapperSelector) : wrapper;
    const descriptionText = container.querySelector(textSelector);
    const showMoreButton = container.querySelector(buttonSelector);
    
    // Use innerWrapper if it exists, otherwise use wrapper
    const targetElement = innerWrapper || wrapper;
    
    if (!targetElement || !descriptionText || !showMoreButton) {
      return;
    }
    
    const showMoreText = showMoreButton.querySelector(showMoreTextSelector);
    const showLessText = showMoreButton.querySelector(showLessTextSelector);
    
    if (!showMoreText || !showLessText) return;
    
    // Measure the full height of the text
    const measureHeight = () => {
      // Temporarily remove height restriction to measure
      targetElement.style.maxHeight = 'none';
      const fullHeight = targetElement.scrollHeight;
      targetElement.style.maxHeight = '';
      return fullHeight;
    };
    
    const fullHeight = measureHeight();
    
    // Set initial collapsed state
    targetElement.style.maxHeight = collapsedHeight + 'px';
    targetElement.classList.add(collapsedClass);
    
    // Check if text exceeds collapsed height
    const checkTextHeight = () => {
      // Show button only if text is strictly greater than collapsed height
      // (hide if text has 3 lines or less - fullHeight <= 72px)
      if (fullHeight > collapsedHeight) {
        showMoreButton.style.display = 'block';
        targetElement.classList.add('has-overflow');
      } else {
        showMoreButton.style.display = 'none';
        targetElement.classList.remove('has-overflow');
      }
    };
    
    // Check on load and resize
    setTimeout(checkTextHeight, 100);
    window.addEventListener('resize', () => {
      const newHeight = measureHeight();
      if (!targetElement.classList.contains(collapsedClass)) {
        targetElement.style.maxHeight = newHeight + 'px';
      }
      checkTextHeight();
    });
    
    // Toggle functionality
    showMoreButton.addEventListener('click', function(e) {
      e.preventDefault();
      const isExpanded = this.getAttribute('aria-expanded') === 'true';
      
      if (isExpanded) {
        // Collapse - first remove collapsed class to allow smooth transition
        targetElement.classList.remove(collapsedClass);
        // Force reflow
        targetElement.offsetHeight;
        // Start collapse animation
        requestAnimationFrame(() => {
          targetElement.style.maxHeight = collapsedHeight + 'px';
        });
        
        // Add collapsed class and update button after animation
        targetElement.addEventListener('transitionend', function onTransitionEnd(e) {
          // Only handle max-height transition end
          if (e.propertyName === 'max-height') {
            targetElement.removeEventListener('transitionend', onTransitionEnd);
            targetElement.classList.add(collapsedClass);
            showMoreText.style.display = 'inline';
            showLessText.style.display = 'none';
            showMoreButton.setAttribute('aria-expanded', 'false');
          }
        });
      } else {
        // Expand
        targetElement.classList.remove(collapsedClass);
        targetElement.style.maxHeight = fullHeight + 'px';
        
        showMoreText.style.display = 'none';
        showLessText.style.display = 'inline';
        this.setAttribute('aria-expanded', 'true');
      }
    });
  });
}

